import React, { useState, useEffect, useContext } from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { Col, NavItem, NavLink, Button, Container, Row } from 'reactstrap';
import { toBlog, toBlogPostDetail } from '../../routes';
import { dpOrange } from '../../utils/displayHelper';
import { BlogPostFeaturedListType } from '../../types/wordpress';
import dayjs from 'dayjs';
import { FaCaretDown } from 'react-icons/fa';
import { MARKETING_NAV } from './UnauthenticatedLayout';
import { isEmployerType, UserContext } from '../../context/UserContext';

const FlyoutMenu = () => {
  const { employerPosts, staffPosts, unauthenticatedPosts } =
    useStaticQuery(blogQuery);
  const { user, isLoading } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState('');
  const [blogPosts, setBlogPosts] =
    useState<BlogPostFeaturedListType>(unauthenticatedPosts);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (!isLoading && hasMounted) {
      const updatedBlogPosts = !user
        ? unauthenticatedPosts
        : isEmployerType(user)
          ? employerPosts
          : staffPosts;
      setBlogPosts(updatedBlogPosts);
    } else if (!hasMounted) {
      setHasMounted(true);
    }
  }, [user?.username, isLoading, hasMounted]);

  function toggleActiveTab(tabId: string) {
    if (activeTab === tabId) {
      setActiveTab('');
    } else {
      setActiveTab(tabId);
    }
  }

  const getStyle = (tabId: string) => {
    return activeTab === tabId ? hoverStyle : {};
  };

  const hoverStyle = {
    borderBottomColor: dpOrange,
  };

  const defaultBorderStyle = {
    borderBottomColor: 'transparent',
    borderBottomStyle: 'solid' as const,
    borderBottomWidth: '3px',
  };

  return (
    <div
      className="d-none d-lg-flex h-100 justify-content-center align-items-center mx-auto"
      onMouseLeave={() => setActiveTab('')}
    >
      {MARKETING_NAV.map((category, categoryIndex) => {
        const isActive = activeTab === `tab-${categoryIndex}`;
        return (
          <div
            key={`dropdown-${categoryIndex}`}
            className="h-100 d-flex"
            style={{ ...defaultBorderStyle, ...getStyle(`tab-${categoryIndex}`) }}
          >
            <Button
              color="white"
              className="d-flex h-100 align-items-center mx-3 rounded-0"
              onClick={() => toggleActiveTab(`tab-${categoryIndex}`)}
              onMouseEnter={() => setActiveTab(`tab-${categoryIndex}`)}
            >
              {category.title} <FaCaretDown />
            </Button>
            <div
              className={`bg-white w-100 shadow mega-nav-dropdown-content ${
                isActive ? 'd-flex' : 'd-none'
              }`}
              style={{
                position: 'absolute',
                top: '84px',
                left: 0,
                height: isActive ? 'auto' : 0,
                width: '100vw',
                overflow: isActive ? undefined : 'hidden',
                zIndex: 2000,
              }}
              onMouseLeave={() => toggleActiveTab(``)}
            >
              <Container fluid className="p-4">
                <Row>
                  <Col md="5" className="mx-auto">
                    <p className="h3 mb-3">{category.mainNav.header}</p>
                    <p>{category.mainNav.description}</p>
                    <Container fluid className="px-0">
                      <Row className="me-2">
                        {category.mainNav.links.map((link, linkIndex) => {
                          return (
                            <NavItem
                              className="col-6 text-start py-2"
                              key={`desktop-${category.title}-main-link-${linkIndex}`}
                              style={{ listStyle: 'none' }}
                            >
                              {link.href ? (
                                <NavLink
                                  href={link.href}
                                  className="text-primary"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => toggleActiveTab('')}
                                >
                                  {link.label}
                                </NavLink>
                              ) : (
                                <NavLink
                                  tag={Link}
                                  className="text-primary"
                                  to={link.route}
                                  onClick={() => toggleActiveTab('')}
                                >
                                  {link.label}
                                </NavLink>
                              )}
                            </NavItem>
                          );
                        })}
                      </Row>
                    </Container>
                    {!!category.mainNav.cta && (
                      <div className="mt-5">
                        <Button
                          color="primary"
                          tag={Link}
                          to={category.mainNav.cta.route}
                          onClick={() => toggleActiveTab('')}
                        >
                          {category.mainNav.cta.label}
                        </Button>
                      </div>
                    )}
                  </Col>
                  <Col xs="auto" className="border-end ps-0">
                    {' '}
                  </Col>
                  <Col md="6" className="mx-auto">
                    {!!category.featureNav && (
                      <>
                        <p className="h3 mb-3">{category.featureNav.header}</p>
                        {category.featureNav.links.map((link, linkIndex) => {
                          return (
                            <NavItem
                              className="py-2"
                              key={`desktop-${category.title}-feature-link-${linkIndex}`}
                              style={{ listStyle: 'none' }}
                            >
                              <NavLink
                                tag={Link}
                                to={link.route}
                                onClick={() => toggleActiveTab('')}
                              >
                                <Container fluid className="px-0 text-black">
                                  <Row>
                                    <Col xs="auto">
                                      <div style={{ width: '50px' }}>
                                        {link.icon}
                                      </div>
                                    </Col>
                                    <Col className="text-left ps-0">
                                      <p className="fw-bold mb-0">
                                        {link.header}
                                      </p>
                                      <p className="mb-0">
                                        {link.description}{' '}
                                        <span className="text-primary">
                                          Learn More
                                        </span>
                                      </p>
                                    </Col>
                                  </Row>
                                </Container>
                              </NavLink>
                            </NavItem>
                          );
                        })}
                      </>
                    )}
                    {category.blogNav && (
                      <>
                        <div className="d-flex mb-3 align-items-center">
                          <p className="h3 mb-3">Our Blog</p>
                          <Button
                            tag={Link}
                            to={toBlog}
                            className="ms-auto"
                            color="primary"
                            onClick={() => toggleActiveTab('')}
                          >
                            View all blog posts
                          </Button>
                        </div>
                        {!!blogPosts &&
                          blogPosts.nodes.map((blog) => {
                            const {
                              id,
                              slug,
                              title,
                              featuredImage: {
                                node: { altText, localFile },
                              },
                              date,
                            } = blog;
                            return (
                              <NavItem
                                key={`desktop-blog-link-${id}`}
                                style={{ listStyle: 'none' }}
                                className="mb-3"
                              >
                                <NavLink
                                  tag={Link}
                                  to={toBlogPostDetail(slug)}
                                  onClick={() => toggleActiveTab('')}
                                >
                                  <Container fluid className="px-0">
                                    <Row>
                                      <Col
                                        xs="auto"
                                        style={{ maxWidth: '200px' }}
                                        className="d-flex align-items-center"
                                      >
                                        {!!localFile?.publicURL && (
                                          <img
                                            loading="lazy"
                                            src={localFile.publicURL}
                                            alt={altText}
                                            style={{ width: '100%', aspectRatio: '3/1', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                                          />
                                        )}
                                      </Col>
                                      <Col className="text-left text-black">
                                        <p className="fw-bold mb-0">{title}</p>
                                        <p className="mb-0">
                                          <small>
                                            Posted{' '}
                                            {dayjs(date).format(
                                              'MMMM DD, YYYY'
                                            )}
                                          </small>
                                        </p>
                                      </Col>
                                    </Row>
                                  </Container>
                                </NavLink>
                              </NavItem>
                            );
                          })}
                      </>
                    )}
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FlyoutMenu;

export const blogQuery = graphql`
  query {
    employerPosts: allWpPost(
      limit: 3
      filter: {
        categories: { nodes: { elemMatch: { databaseId: { eq: 7804 } } } }
      }
      sort: { date: DESC }
    ) {
      nodes {
        ...BlogPostFields
      }
    }
    staffPosts: allWpPost(
      limit: 3
      filter: {
        categories: { nodes: { elemMatch: { databaseId: { eq: 7786 } } } }
      }
      sort: { date: DESC }
    ) {
      nodes {
        ...BlogPostFields
      }
    }
    unauthenticatedPosts: allWpPost(
      limit: 3
      filter: {
        categories: { nodes: { elemMatch: { databaseId: { eq: 4 } } } }
      }
      sort: { date: DESC }
    ) {
      nodes {
        ...BlogPostFields
      }
    }
  }
`;
